import type { Workspace } from '../types/workspace'
import { AVAILABLE_VIEW_KEYS } from '../types/schema'
import { createDefaultWorkspaceLimits } from '../utils/workspaceLimits'
import { normalizeChecklistInputLimits } from '../utils/checklistLimits'
import { DEFAULT_WORKSPACE_ACTION_PERMISSIONS } from '../utils/workspacePermissions'
import { MINIMALIST_AUDIT_CATEGORIES } from '../services/auditCategories'
import { normalizeChecklistBlockedTodoPolicy } from '../utils/checklistBlockedTodoPolicy'
import { DEFAULT_RAID_BLUEPRINT } from '../utils/raidBlueprint'
import { DEBUG_USER } from './fixtures'

export const DEBUG_WORKSPACE_ID = 'debug-personal-workspace'
export const DEBUG_TEAM_WORKSPACE_ID = 'debug-team-workspace'

const workspaceLimits = createDefaultWorkspaceLimits(true)
const teamWorkspaceLimits = createDefaultWorkspaceLimits(false)
// createDefaultWorkspaceLimits drops descriptionMaxLength / completionCriteriaMaxLength;
// normalizeChecklistInputLimits backfills them to the team defaults.
const personalChecklistInputLimits = normalizeChecklistInputLimits(workspaceLimits.input, true)
const teamChecklistInputLimits = normalizeChecklistInputLimits(teamWorkspaceLimits.input, false)

export const DEBUG_WORKSPACE: Workspace = {
  id: DEBUG_WORKSPACE_ID,
  name: 'Personal Space',
  slug: '',
  ownerId: DEBUG_USER.uid,
  kind: 'PERSONAL',
  credits: 0,
  plan: 'FREE_TIER',
  subscription: { status: 'ACTIVE', periodEnd: null, providerSubscriptionId: '' },
  limits: { workspacesCount: 1, storageUsedBytes: 0, lastResetAt: null },
  timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
  isPersonal: true,
  isPublic: false,
  workspaceLimits,
  checklistInputLimits: personalChecklistInputLimits,
  enabledDashboardViews: [...AVAILABLE_VIEW_KEYS],
  defaultDashboardView: AVAILABLE_VIEW_KEYS[0],
  actionPermissions: { ...DEFAULT_WORKSPACE_ACTION_PERMISSIONS },
  auditSettings: { retentionMode: 'STANDARD', fullAuditEnabled: false, lifecycleAlertEnabled: true, enabledCategories: [...MINIMALIST_AUDIT_CATEGORIES] },
  cardBackgroundSettings: { enabled: true },
  checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(),
  raidBlueprint: DEFAULT_RAID_BLUEPRINT,
}

/**
 * Synthetic TEAM workspace for debug bypass. Seeded as ADMIN-owned so the
 * Invites Hub, MemberDashboard, and chat surfaces render fully. Switch into
 * this workspace from the workspace switcher when bypass is active.
 */
export const DEBUG_TEAM_WORKSPACE: Workspace = {
  id: DEBUG_TEAM_WORKSPACE_ID,
  name: 'Debug Team',
  slug: 'debug-team',
  ownerId: DEBUG_USER.uid,
  kind: 'TEAM',
  credits: 5000,
  plan: 'GROWTH_TIER',
  subscription: { status: 'ACTIVE', periodEnd: null, providerSubscriptionId: '' },
  limits: { workspacesCount: 1, storageUsedBytes: 0, lastResetAt: null },
  timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
  isPersonal: false,
  isPublic: false,
  workspaceLimits: teamWorkspaceLimits,
  checklistInputLimits: teamChecklistInputLimits,
  enabledDashboardViews: [...AVAILABLE_VIEW_KEYS],
  defaultDashboardView: AVAILABLE_VIEW_KEYS[0],
  actionPermissions: { ...DEFAULT_WORKSPACE_ACTION_PERMISSIONS },
  auditSettings: { retentionMode: 'STANDARD', fullAuditEnabled: false, lifecycleAlertEnabled: true, enabledCategories: [...MINIMALIST_AUDIT_CATEGORIES] },
  cardBackgroundSettings: { enabled: true },
  checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(),
  raidBlueprint: DEFAULT_RAID_BLUEPRINT,
  // Enable premium modules for mock preview + PR2/PR3 verification
  modules: { budgetEnabled: true, stockEnabled: true, quotaEnabled: true },
  settings: { currency: 'USD' },
}
